import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconMoreVertical, IconSettings, IconLogOut, IconSun, IconMoon, IconUser } from '@/components/icons/Icons'
import { useTheme } from '@/hooks/useTheme'

/**
 * El pie del sidebar: quién está usando el ERP.
 *
 * Antes terminaba en un botón de «Cerrar Sesión» y nada más, porque la app no tenía con
 * qué decir quién sos: `usuarios` guardaba email, hash y rol. Ahora guarda nombre y foto,
 * y acá se usan.
 *
 * Los tres puntos se llevan lo que no es de todos los días —Configuración y salir—, que
 * es justamente lo que antes ocupaba un lugar fijo en el menú principal compitiendo con
 * el trabajo.
 */

const ROLES: Record<string, string> = {
  admin: 'Administrador',
  partner: 'Partner',
  cliente: 'Cliente',
}

interface Props {
  nombre: string | null | undefined
  email: string
  rol: string | null | undefined
  fotoUrl: string | null | undefined
  colapsado: boolean
  onCerrarSesion: () => void
  /**
   * Los items del menu, de arriba hacia abajo. El ERP lleva uno solo
   * (Configuracion); el panel del partner lleva dos —Mi perfil y Ajustes—
   * porque ahi son dos pantallas distintas: una es la persona y la otra las
   * integraciones de su cuenta.
   *
   * Es una lista y un parametro, no una rama dentro del componente, porque el
   * pie del sidebar no deberia tener que saber en que panel esta montado.
   */
  enlaces?: { label: string; to: string; icono?: 'perfil' | 'ajustes' }[]
  /**
   * El interruptor de tema. Va acá en el ERP, donde el engranaje lleva a
   * Configuracion —otra pantalla, y nuestra—; en el panel del partner se
   * apaga, porque ahi el tema vive dentro de Ajustes.
   */
  tema?: boolean
}

/**
 * Dos iniciales del nombre, o una del correo. Es el mismo recurso de Gmail y Slack:
 * sin foto, igual hay algo que identifica y el pie no queda cojo.
 */
function iniciales(nombre: string | null | undefined, email: string): string {
  const limpio = (nombre || '').trim()
  if (limpio) {
    const partes = limpio.split(/\s+/).filter(Boolean)
    if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase()
    return partes[0].slice(0, 2).toUpperCase()
  }
  return (email || '?').trim().charAt(0).toUpperCase()
}

const ENLACES_ERP: Props['enlaces'] = [{ label: 'Configuración', to: '/configuracion', icono: 'ajustes' }]

export default function UserMenu({
  nombre, email, rol, fotoUrl, colapsado, onCerrarSesion,
  enlaces = ENLACES_ERP, tema: mostrarTema = true,
}: Props) {
  const [abierto, setAbierto] = useState(false)
  const contenedor = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const [tema, alternarTema] = useTheme()

  useEffect(() => {
    if (!abierto) return

    const alClickAfuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setAbierto(false)
    }
    // Escape además del clic: el menú tapa parte del sidebar y quedarse sin salida con
    // el teclado es exactamente el tipo de detalle que vuelve molesta una interfaz.
    const alEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false) }

    document.addEventListener('mousedown', alClickAfuera)
    document.addEventListener('keydown', alEscape)
    return () => {
      document.removeEventListener('mousedown', alClickAfuera)
      document.removeEventListener('keydown', alEscape)
    }
  }, [abierto])

  const mostrado = (nombre || '').trim() || email
  const rolTexto = ROLES[String(rol)] || 'Usuario'

  const ir = (to: string) => {
    setAbierto(false)
    navigate(to)
  }

  return (
    <div
      className={`user-menu${colapsado ? ' user-menu--colapsado' : ''}`}
      ref={contenedor}
    >
      <button
        className="user-menu__perfil"
        onClick={() => setAbierto(v => !v)}
        type="button"
        // Colapsado el nombre no se ve, así que el título es la única forma de saber
        // con qué cuenta se está trabajando.
        title={colapsado ? `${mostrado} · ${rolTexto}` : email}
        aria-haspopup="menu"
        aria-expanded={abierto}
      >
        <span className="user-menu__avatar">
          {fotoUrl
            ? <img src={fotoUrl} alt="" className="user-menu__foto" />
            : iniciales(nombre, email)}
        </span>

        {!colapsado && (
          <span className="user-menu__datos">
            <span className="user-menu__nombre">{mostrado}</span>
            <span className="user-menu__rol">{rolTexto}</span>
          </span>
        )}

        {!colapsado && (
          <span className="user-menu__puntos" aria-hidden="true">
            <IconMoreVertical size={16} />
          </span>
        )}
      </button>

      {abierto && (
        <div className="user-menu__popover" role="menu">
          {/* El ícono y el texto nombran el tema al que se VA, no el actual:
              un control que dibuja el estado presente deja al usuario
              adivinando qué pasa si lo pulsa. El menú no se cierra, para que
              el cambio se vea de inmediato y se pueda deshacer en el acto. */}
          {mostrarTema && (
            <button className="user-menu__item" onClick={alternarTema} type="button" role="menuitem">
              {tema === 'dark'
                ? <><IconSun size={15} /> Tema claro</>
                : <><IconMoon size={15} /> Tema oscuro</>}
            </button>
          )}
          {(enlaces ?? []).map(enlace => (
            <button
              key={enlace.to}
              className="user-menu__item"
              onClick={() => ir(enlace.to)}
              type="button"
              role="menuitem"
            >
              {enlace.icono === 'perfil' ? <IconUser size={15} /> : <IconSettings size={15} />}
              {' '}{enlace.label}
            </button>
          ))}
          <button
            className="user-menu__item user-menu__item--salir"
            onClick={() => { setAbierto(false); onCerrarSesion() }}
            type="button"
            role="menuitem"
          >
            <IconLogOut size={15} /> Cerrar Sesión
          </button>
        </div>
      )}
    </div>
  )
}
